/* eslint-disable jsx-a11y/alt-text */
import { createColumnHelper } from "@tanstack/react-table";
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchProductGroupDownload,
  useProductGroupList,
} from "../../../../api/product-group/use-product-group";
import { FallbackImage } from "../../../../assets/img";
import Table from "../../../../components/ui/Table/Table";

const columnHelper = createColumnHelper();

const columns = (page, pageSize, total) => {
  return [
    columnHelper.accessor((row) => row, {
      header: "번호",
      cell: (info) => {
        const index = info.cell.row.index;
        const indexVal =
          page === 1 ? total - index : total - index - (page - 1) * pageSize;

        return <div>{indexVal}</div>;
      },
    }),

    columnHelper.accessor((row) => row?.productGroup?.productNo, {
      header: "상품번호",
      cell: (info) => {
        const val = info.getValue();
        const prodNo = val?.length > 10 ? val?.substring(0, 10) + "..." : val;

        return <div>{val ? prodNo : "-"}</div>;
      },
    }),

    columnHelper.accessor((row) => row.productGroup.mainImage, {
      header: "이미지",
      cell: (info) => {
        let val = info.getValue();
        const handleError = ({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = FallbackImage;
        };
        return (
          <div className="m-auto w-[100px] h-[100px] flex justify-center">
            <img
              className="m-auto w-[85px] h-[85px] border border-primary-grey object-cover"
              src={val}
              onError={handleError}
            />
          </div>
        );
      },
    }),

    columnHelper.accessor((row) => row, {
      header: "상품 그룹명/상품명",
      cell: (info) => {
        const name = info.getValue().name;
        const productGroup = info.getValue().productGroup.name;
        const prodNameSliced =
          name?.length > 20 ? name?.substring(0, 20) + "..." : name;
        const prodGroupSliced =
          productGroup?.length > 20
            ? productGroup?.substring(0, 20) + "..."
            : productGroup;

        return (
          <>
            <span className="text-[#333333]">{prodGroupSliced}</span>
            <br />
            <span className="text-[#aaaaaa]">{prodNameSliced}</span>
          </>
        );
      },
    }),

    columnHelper.accessor((row) => row?.productGroup?.productGroupCategory, {
      header: "카테고리",
      cell: (info) => {
        const val = info.getValue().name;
        return <div>{val ? val : "-"}</div>;
      },
    }),

    columnHelper.accessor((row) => row.oriPrice, {
      header: "정상 판매가",
      cell: (info) => {
        const val = parseFloat(info.getValue()).toFixed(0);
        return (
          <div>{val ? parseInt(val).toLocaleString("ko-KR") + "원" : "-"}</div>
        );
      },
    }),

    columnHelper.accessor((row) => row?.stock, {
      header: "재고",
      cell: (info) => {
        const val = info.getValue();
        return <div>{val ? val : "-"}</div>;
      },
    }),

    columnHelper.accessor((row) => row?.saleStatus, {
      header: "판매 상태",
      cell: (info) => {
        const val = info.getValue();
        const saleVal = val === "SALE" ? "정상" : "판매종료";
        return <div>{val ? saleVal : "-"}</div>;
      },
    }),

    columnHelper.accessor((row) => row?.productGroup?.status, {
      header: "노출 상태",
      cell: (info) => {
        const val = info.getValue();
        const statusVal = val === "ON" ? "Y" : "N";
        return <div>{val ? statusVal : "-"}</div>;
      },
    }),

    columnHelper.accessor((row) => row?.productGroup?.pointPurchasePercent, {
      header: "결제 적립",
      cell: (info) => {
        const val = info.getValue();
        return <div>{val ? `${val}%` : "-"}</div>;
      },
    }),

    columnHelper.accessor((row) => row?.productGroup?.pointReview, {
      header: "리뷰 적립",
      cell: (info) => {
        const val = info.getValue();
        return <div>{val ? `${val}P` : "-"}</div>;
      },
    }),

    columnHelper.accessor((row) => row?.productGroup?.discountStatus, {
      header: "할인",
      cell: (info) => {
        const val = info.getValue();
        const renderDisc = (disc) => {
          switch (disc) {
            case "DATES":
              return "특정기간만 할인";
            case "EVERY":
              return "전체기간 할인";
            case "OFF":
              return "할인 미적용";
            default:
              break;
          }
        };
        return <div>{val ? renderDisc(val) : "-"}</div>;
      },
    }),

    columnHelper.accessor((row) => row, {
      header: "할인기간",
      cell: (info) => {
        const from = info
          .getValue()
          .productGroup?.discountStartDate?.slice(0, 10);
        const to = info.getValue().productGroup?.discountEndDate?.slice(0, 10);
        return (
          <div>
            {from ? (
              <div>
                {from} <br />~ {to}
              </div>
            ) : (
              "-"
            )}
          </div>
        );
      },
    }),

    columnHelper.accessor((row) => row, {
      header: "업데이트일",
      cell: (info) => {
        const val = info.getValue().productGroup?.updatedAt;
        const date = val?.slice(0, 10);
        const time = val?.split("T")[1]?.slice(0, 5);
        return (
          <div>
            {val ? (
              <>
                <span>{date}</span>
                <br />
                <span>{time}</span>
              </>
            ) : (
              "-"
            )}
          </div>
        );
      },
    }),
    columnHelper.display({
      header: "관리",
      id: "actions",
      cell: ButtonActions,
    }),
  ];
};

function ButtonActions(props) {
  const id = props.row.original?.productGroup?.id;
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(`${id}`)}
      className="w-12 p-[3px] border border-[#cccccc] rounded-[4px] text-[#333333]"
    >
      상세
    </button>
  );
}

export function ProductGroupTable(props) {
  const { filter, isResettedPage } = props;
  const { isEmpty, getDataTableProps } = useProductGroupList(filter);
  const currentPage = getDataTableProps().pagination?.pageIndex + 1;
  const pageSize = getDataTableProps().pagination?.pageSize;
  const totalData = getDataTableProps().total;

  const sorts = [
    { id: 1, name: "최신순" },
    { id: 2, name: "등록일순" },
    { id: 3, name: "판매량 많은 순" },
  ];

  const onSave = async () => {
    await fetchProductGroupDownload().then((response) => {
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `product-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
    });
  };
  return (
    <>
      {isEmpty ? (
        <div className="flex justify-center mt-[60px] text-[#999999] text-center">
          등록된 상품이 없습니다.
          <br /> ONEDAY Mall에 상품을 등록하실 경우, '+ 상품등록'을
          진행해주세요.
        </div>
      ) : (
        <>
          <Table
            onSave={onSave}
            sorts={sorts}
            columns={columns(currentPage, pageSize, totalData)}
            width="w-[8rem]"
            dropdownWidth="w-[8rem]"
            isResettedPage={isResettedPage}
            {...getDataTableProps()}
          />
        </>
      )}
    </>
  );
}
